import { useEffect, useRef, useState } from 'react';
import LiveVideo from '../components/ocgInput/liveVideo';
import { usePageTitle } from '../appInfo';
import { clientIdFromHost, cropFrame, CROP_MAX, hasSdkParams, loadCrop, saveCrop, VIEWS, viewUrl } from '../utils/discordActivity';

/** 映像が来ていないと判断するまでの時間（ms） */
const STALL_MS = 4000;

/**
 * **Discord の Activity のページ**（Issue #687）: 補正サーバーの映像（`/live/stream`）を全画面で流すだけ。ログインも本番の API も使わない
 * （`/live/stream` はログイン無しで読める）。ライブなので再生位置はそろえない（各自がライブの端を見る）。
 * Discord の中では SDK の `ready()` を呼んで読み込みの画面を閉じてもらう（ユーザーの情報は読まない＝ authorize しない）。
 * Discord の外（`/discord-activity` を直に開いた確かめ）では SDK を使わない。
 * 上下の切り抜きは「切り抜き」から手で決め、見るもの（実写 / ゲーム）ごとにその端末に保存する（`loadCrop` / `saveCrop`）。
 * 映像はいつも同じ origin の /live（覚えている補正サーバーの口を使わない）。
 */
export default function DiscordActivity({ sdkLoader = () => import('@discord/embedded-app-sdk'), VideoImpl = LiveVideo }) {
  usePageTitle('みんなで見る');
  const box = useRef(null);
  const canvasRef = useRef(null);
  const [view, setView] = useState('live');
  const [crop, setCrop] = useState(() => loadCrop('live'));
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [editing, setEditing] = useState(false);
  const [state, setState] = useState('connecting');   // connecting / playing / waiting / unsupported
  const [sdkNote, setSdkNote] = useState(null);

  // Discord の SDK: 読み込みの画面を閉じてもらう
  useEffect(() => {
    const id = clientIdFromHost(window.location.hostname);
    if (!id || !hasSdkParams(window.location.search)) return;
    let gone = false;
    sdkLoader()
      .then(({ DiscordSDK }) => new DiscordSDK(id).ready())
      .catch((e) => { if (!gone) setSdkNote(`Discord とつなげません（${e?.message ?? e}）`); });
    return () => { gone = true; };
  }, [sdkLoader]);

  // 映像: ライブの端から流す。切れたら LiveVideo が自分でつなぎ直す。しばらく絵が来なければ「待っています」
  useEffect(() => {
    const video = new VideoImpl({ url: viewUrl(view), sameOrigin: true });   // 覚えている補正サーバーの口を使わない（Discord の中は同じ origin しか通らない）
    if (!video.supported) { setState('unsupported'); return undefined; }
    const canvas = video.drawSource;
    canvas.className = 'discord-activity-video';
    box.current?.appendChild(canvas);
    canvasRef.current = canvas;
    let last = Date.now();
    const onFrame = () => { last = Date.now(); setState('playing'); };
    video.addEventListener('loadedmetadata', () => setSize({ w: video.videoWidth, h: video.videoHeight }));
    video.addEventListener('timeupdate', onFrame);
    video.addEventListener('loadeddata', onFrame);
    setState('connecting');
    video.start();
    const timer = setInterval(() => { if (Date.now() - last > STALL_MS) setState('waiting'); }, 1000);
    return () => {
      clearInterval(timer);
      video.destroy();
      canvas.remove();
      canvasRef.current = null;
    };
  }, [view, VideoImpl]);

  // 切り抜き: 枠の中で映像を上へずらして大きくし、はみ出した上下を枠が隠す
  const frame = cropFrame(size.w, size.h, crop);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.style.height = `${frame.heightPct}%`;
    c.style.top = `${frame.topPct}%`;
  });

  const pickView = (key) => { setView(key); setCrop(loadCrop(key)); };
  const changeCrop = (side, value) => {
    const next = { ...crop, [side]: value };
    setCrop(next);
    saveCrop(view, next);
  };

  const note = state === 'unsupported' ? 'この端末では映像を再生できません（VideoDecoder が無い）'
    : state === 'waiting' ? (view === 'game' ? 'ゲーム映像を待っています（録画中で、描く側が動いているときに出ます）' : '映像を待っています（補正サーバーが止まっているか、撮影していません）')
      : state === 'connecting' ? 'つないでいます…' : null;

  return (
    <div className="discord-activity" data-testid="discord-activity">
      <div className="discord-activity-stage">
        <div ref={box} className="discord-activity-frame" data-testid="discord-activity-frame"
          style={{ aspectRatio: String(frame.ratio), width: `min(100vw, calc(100vh * ${frame.ratio}))` }} />
      </div>
      {note && <div className="discord-activity-note">{note}</div>}
      {sdkNote && <div className="discord-activity-note discord-activity-sdk">{sdkNote}</div>}
      <div className="discord-activity-views" role="group" aria-label="見るもの">
        <button type="button" aria-pressed={editing} onClick={() => setEditing(!editing)}>切り抜き</button>
        {VIEWS.map((v) => (
          <button key={v.key} type="button" aria-pressed={view === v.key} onClick={() => pickView(v.key)}>{v.label}</button>
        ))}
      </div>
      {editing && (
        <div className="discord-activity-crop" role="group" aria-label="上下の切り抜き">
          {[['top', '上'], ['bottom', '下']].map(([side, label]) => (
            <label key={side}>
              {label} {Math.round(crop[side] * 100)}%
              <input type="range" min="0" max={CROP_MAX} step="0.01" value={crop[side]} aria-label={`${label}を切る`}
                onChange={(e) => changeCrop(side, Number(e.target.value))} />
            </label>
          ))}
          <button type="button" onClick={() => { setCrop({ top: 0, bottom: 0 }); saveCrop(view, { top: 0, bottom: 0 }); }}>元に戻す</button>
        </div>
      )}
    </div>
  );
}
